import React from 'react';
import { Cross2Icon } from "@radix-ui/react-icons";
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { RootState } from '../../store/store';
import { closeImage } from '../../store/chatSlice';
import logger from '../../utils/logger/logger';

const IMAGE_MODAL_ANIMATION_MS = 240;

function buildPdfViewerSrc(src: string, zoom: number = 100) {
  void zoom;
  return `${src}#toolbar=1&navpanes=1&pagemode=thumbs`;
}

function pdfViewerNameOverlay(fileName: string, pdfSrc?: string) {
  void pdfSrc;
  const overlayWidth = 'clamp(180px, 17vw, 245px)';
  const overlayCoverWidth = 'clamp(195px, 18vw, 265px)';
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: overlayCoverWidth,
          height: '64px',
          zIndex: 3,
          background: 'linear-gradient(90deg, rgb(60, 60, 60) 0%, rgb(60, 60, 60) 90%, rgba(60, 60, 60, 0.94) 96%, rgba(60, 60, 60, 0) 100%)',
          pointerEvents: 'none'
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '8px',
          left: '0',
          width: overlayWidth,
          height: '48px',
          zIndex: 4,
          color: '#fff',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          padding: '0 14px 0 18px',
          boxSizing: 'border-box'
        }}
        title={fileName}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '3px',
            width: '18px',
            height: '18px',
            marginRight: '12px',
            opacity: 0.95,
            flexShrink: 0
          }}
        >
          {[0, 1, 2].map((line) => (
            <span
              key={line}
              style={{
                display: 'block',
                width: '12px',
                height: '2px',
                borderRadius: '999px',
                background: 'rgba(255, 255, 255, 0.92)'
              }}
            />
          ))}
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            maxWidth: 'calc(100% - 36px)',
            lineHeight: 1
          }}
        >
          <span
            style={{
              fontSize: '0.88rem',
              fontWeight: 600,
              color: '#fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: '1 1 auto',
              minWidth: 0
            }}
          >
            {fileName}
          </span>
        </span>
      </div>
    </>
  );
}

export function ImageModal() {
  const dispatch = useDispatch();
  const imageSrc = useSelector((state: RootState) => state.chat.openImageSrc);
  const imageType = useSelector((state: RootState) => state.chat.openImageType);
  const imageName = useSelector((state: RootState) => state.chat.openImageName);
  const useCustomPdfHeader = useSelector((state: RootState) => state.chat.openImageUseCustomPdfHeader);
  const isOpen = !!imageSrc;
  const [displayedSrc, setDisplayedSrc] = React.useState<string | null>(null);
  const [displayedType, setDisplayedType] = React.useState<'image' | 'pdf' | null>(null);
  const [displayedName, setDisplayedName] = React.useState<string | null>(null);
  const [displayedUseCustomPdfHeader, setDisplayedUseCustomPdfHeader] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  
  // Use the type from Redux store
  const isPDF = displayedType === 'pdf';
  const shouldUseCustomPdfHeader = displayedUseCustomPdfHeader || (isPDF && !!displayedName && !/\.pdf$/i.test(displayedName));
  
  logger.log('[ImageModal] Rendering:', { isOpen, isPDF, imageSrc: imageSrc?.substring(0, 50), imageType });

  const handleClose = React.useCallback(() => {
    // Start exit animation
    setIsVisible(false);
    // After animation completes, unmount and clear Redux state
    setTimeout(() => {
      setShouldRender(false);
      setDisplayedSrc(null);
      setDisplayedType(null);
      setDisplayedName(null);
      setDisplayedUseCustomPdfHeader(false);
      dispatch(closeImage());
    }, IMAGE_MODAL_ANIMATION_MS);
  }, [dispatch]);

  // Handle opening: when Redux state says open, mount and animate in
  React.useEffect(() => {
    if (isOpen && imageSrc) {
      setDisplayedSrc(imageSrc);
      setDisplayedType(imageType || 'image');
      setDisplayedName(imageName || null);
      setDisplayedUseCustomPdfHeader(!!useCustomPdfHeader);
      setShouldRender(true);
      const rafId = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(rafId);
    }
    return undefined;
  }, [imageName, imageSrc, imageType, isOpen, useCustomPdfHeader]);

  // Handle escape key and prevent scrolling
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      // Prevent wheel events on the backdrop from propagating
      const target = e.target as HTMLElement;
      // Only prevent if not scrolling inside the PDF iframe container
      if (!target.closest('[data-pdf-container]')) {
        e.preventDefault();
      }
    };

    if (shouldRender) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('wheel', handleWheel, { passive: false });
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'unset';
    };
  }, [handleClose, shouldRender]);

  if (typeof document === 'undefined' || !shouldRender || !displayedSrc) return null;

  return (
    <motion.div 
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.8)', 
        backdropFilter: 'blur(4px)',
        overflow: 'hidden'
      }}
      onClick={handleClose}
      initial={false}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
    >
      <motion.button
        onClick={handleClose}
        className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          padding: '8px',
          borderRadius: '6px',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px'
        }}
        initial={false}
        animate={{ opacity: isVisible ? 0.7 : 0, y: isVisible ? 0 : -8 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <Cross2Icon className="h-6 w-6" />
        <span className="sr-only">Close</span>
      </motion.button>

      {isPDF ? (
        <motion.div
          data-pdf-container
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '90vw',
            height: '90vh',
            borderRadius: '0px',
            overflow: 'hidden',
            position: 'relative',
            background: '#111827',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.35)'
          }}
          initial={false}
          animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.985, y: isVisible ? 0 : 10 }}
          transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
        >
          {shouldUseCustomPdfHeader && displayedName ? pdfViewerNameOverlay(displayedName, displayedSrc || undefined) : null}
          <iframe
            src={buildPdfViewerSrc(displayedSrc, 100)}
            title={displayedName || 'PDF document viewer'}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: 'white',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          />
        </motion.div>
      ) : (
        <motion.div
          style={{
            width: '90vw',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start'
          }}
          initial={false}
          animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.985, y: isVisible ? 0 : 10 }}
          transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
        >
          {displayedName && (
            <div
              style={{
                margin: '0 64px 12px 0',
                zIndex: 1,
                padding: '8px 12px',
                background: 'rgba(0, 0, 0, 0.55)',
                color: '#fff',
                fontSize: '0.95rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderRadius: '8px'
              }}
              title={displayedName}
            >
              {displayedName}
            </div>
          )}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img
              src={displayedSrc}
              alt={displayedName || 'Full screen view'}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
            />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}